/**
 * The control a filter's value is edited with.
 *
 * One component per kind rather than one per filter type: ninety-one filters
 * share thirteen ways of naming a value, and `filterInputKind` is the map from
 * one to the other. It answers for every filter type, so a filter added on the
 * backend fails to compile before it can render as a box that writes the wrong
 * shape.
 *
 * **Every value is a string**, whatever the control looks like, because that is
 * what a `ViewFilter` stores. The multi-step date filters make the most of that
 * -- theirs is `timezone?value?operator`, three fields packed into one -- which
 * is why they are the only kind here with any parsing in them.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewFilterTypeName } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import {
  DATE_FILTER_VALUE_SEPARATOR as SEP,
  splitMultiStepDateValue,
  splitTimezoneAndValue,
  type DateOperator,
} from '../filters/date-filters'
import { VIEW_FILTER_TYPES } from '../filters'
import { filterInputKind } from '../filters/inputs'
import { selectOptionsOf } from '../fields/ui/edit'
import { LinkRowInput } from '../fields/ui/edit/link-row'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'

/**
 * How long a typed value waits before it is sent.
 *
 * The same reasoning as the search box: a filter changes which rows the view
 * contains, so every keystroke would otherwise be a request, a refetch and a
 * window thrown away.
 */
export const FILTER_VALUE_DELAY = 400

/** The periods a multi-step date filter can name, in the order they are offered. */
const DATE_OPERATORS: readonly DateOperator[] = [
  'today',
  'yesterday',
  'tomorrow',
  'this_week',
  'one_week_ago',
  'next_week',
  'this_month',
  'one_month_ago',
  'next_month',
  'this_year',
  'one_year_ago',
  'next_year',
  'nr_days_ago',
  'nr_days_from_now',
  'nr_weeks_ago',
  'nr_weeks_from_now',
  'nr_months_ago',
  'nr_months_from_now',
  'nr_years_ago',
  'nr_years_from_now',
  'exact_date',
]

/** The operators that read a number, and the one that reads a date. */
const TAKES_NUMBER = new Set<DateOperator>([
  'nr_days_ago',
  'nr_days_from_now',
  'nr_weeks_ago',
  'nr_weeks_from_now',
  'nr_months_ago',
  'nr_months_from_now',
  'nr_years_ago',
  'nr_years_from_now',
])

/**
 * The parts of a stored condition this file reads.
 *
 * Structural rather than `ViewFilter`, because a form question's visibility
 * conditions are the same shape with two fields fewer -- they belong to a field
 * option rather than to a view, and nothing preloads a display name for them.
 */
export interface FilterValueSource {
  value?: string
  preload_values?: Record<string, unknown>
}

export interface FilterValueProps {
  filter: FilterValueSource
  field: Field
  /** The filter type, which may differ from the stored one while it is changing. */
  type: ViewFilterTypeName
  context: FieldContext
  onChange: (value: string) => void
}

export function FilterValue(props: FilterValueProps) {
  const kind = filterInputKind(props.type, props.field)

  switch (kind) {
    case 'none':
      return null
    case 'boolean':
      return <BooleanValue {...props} />
    case 'select-one':
      return <SelectOneValue {...props} />
    case 'select-many':
      return <SelectManyValue {...props} />
    case 'user':
      return <UserValue {...props} />
    case 'file-type':
      return <FileTypeValue {...props} />
    case 'link-row':
      return <LinkRowValue {...props} />
    case 'date':
      return <DateValue {...props} />
    case 'day-of-month':
      return <DayOfMonthValue {...props} />
    case 'number':
    case 'rating':
      return <TypedValue {...props} inputType="number" />
    default:
      // text, duration and the legacy date filters. A duration is typed the way
      // it is read -- "2:30" -- and the backend parses it, which is the same
      // road a duration cell takes. A legacy filter's value is shown as the
      // text it is: each is a multi-step filter with its operator fixed, and
      // the operator is not something the stored value says.
      return <TypedValue {...props} inputType="text" />
  }
}

/**
 * A box whose value is held here and sent after a pause.
 *
 * The value comes back through the cache, which only changes once the server
 * has answered -- so a box bound straight to it would drop characters typed
 * during the round trip.
 */
function useDraft(
  value: string,
  onChange: (value: string) => void,
): [string, (next: string) => void] {
  const [text, setText] = useState(value)
  const pending = useRef<ReturnType<typeof setTimeout>>(undefined)
  const sent = useRef(value)

  if (value !== sent.current) {
    sent.current = value
    setText(value)
    clearTimeout(pending.current)
  }

  useEffect(
    () => () => {
      clearTimeout(pending.current)
    },
    [],
  )

  return [
    text,
    (next: string) => {
      setText(next)
      clearTimeout(pending.current)
      pending.current = setTimeout(() => {
        sent.current = next
        onChange(next)
      }, FILTER_VALUE_DELAY)
    },
  ]
}

function TypedValue({
  filter,
  field,
  type,
  onChange,
  inputType,
}: FilterValueProps & { inputType: 'text' | 'number' }) {
  const { t } = useTranslation()
  const [text, type_] = useDraft(filter.value ?? '', onChange)
  void type
  return (
    <Input
      type={inputType}
      value={text}
      aria-label={t('view.filterValue', { field: field.name })}
      onChange={(event) => {
        type_(event.target.value)
      }}
      className="h-8 flex-1"
    />
  )
}

function BooleanValue({ filter, field, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  return (
    <NativeSelect
      size="sm"
      className="flex-1"
      aria-label={t('view.filterValue', { field: field.name })}
      value={filter.value === '1' ? '1' : '0'}
      onChange={(event) => {
        onChange(event.target.value)
      }}
    >
      <NativeSelectOption value="1">{t('view.checked')}</NativeSelectOption>
      <NativeSelectOption value="0">{t('view.unchecked')}</NativeSelectOption>
    </NativeSelect>
  )
}

function SelectOneValue({ filter, field, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  return (
    <NativeSelect
      size="sm"
      className="flex-1"
      aria-label={t('view.filterValue', { field: field.name })}
      value={filter.value ?? ''}
      onChange={(event) => {
        onChange(event.target.value)
      }}
    >
      {/* An empty choice, because a select filter with no option chosen has no
          opinion and shows every row -- which is a state somebody has to be
          able to get back to. */}
      <NativeSelectOption value="">{t('view.filterAnyOption')}</NativeSelectOption>
      {selectOptionsOf(field).map((option) => (
        <NativeSelectOption key={option.id} value={option.id}>
          {option.value}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

/** The ids a comma-separated filter value names. */
const idsIn = (value: string): number[] =>
  value
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isSafeInteger(id) && id > 0)

function SelectManyValue({ filter, field, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  const chosen = new Set(idsIn(filter.value ?? ''))

  return (
    <fieldset className="flex-1">
      <legend className="sr-only">{t('view.filterValue', { field: field.name })}</legend>
      <div className="flex max-h-28 flex-col gap-1 overflow-y-auto rounded border p-1">
        {selectOptionsOf(field).map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={chosen.has(option.id)}
              onCheckedChange={(checked) => {
                const next = new Set(chosen)
                if (checked === true) next.add(option.id)
                else next.delete(option.id)
                // Ordered by id rather than by when they were ticked, so the
                // same set of options is always the same stored value.
                onChange([...next].sort((a, b) => a - b).join(','))
              }}
            />
            {option.value}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function UserValue({ filter, field, context, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  return (
    <NativeSelect
      size="sm"
      className="flex-1"
      aria-label={t('view.filterValue', { field: field.name })}
      value={filter.value ?? ''}
      onChange={(event) => {
        onChange(event.target.value)
      }}
    >
      <NativeSelectOption value="">{t('view.filterAnyone')}</NativeSelectOption>
      {(context.collaborators ?? []).map((person) => (
        <NativeSelectOption key={person.id} value={person.id}>
          {person.name}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

/** What the backend's `has_file_type` filter accepts, and nothing else. */
const FILE_TYPES = ['image', 'document'] as const

function FileTypeValue({ filter, field, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  return (
    <NativeSelect
      size="sm"
      className="flex-1"
      aria-label={t('view.filterValue', { field: field.name })}
      value={filter.value ?? ''}
      onChange={(event) => {
        onChange(event.target.value)
      }}
    >
      <NativeSelectOption value="">{t('view.filterAnyFile')}</NativeSelectOption>
      {FILE_TYPES.map((name) => (
        <NativeSelectOption key={name} value={name}>
          {t(`view.fileType_${name}`)}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

/**
 * One row of the related table, named by its id.
 *
 * The cell editor for a link row is reused whole: it already searches the
 * related table, waits for a pause, and drops an answer to a search that has
 * been typed past. What differs is arity -- a filter names one row, a cell
 * holds many -- so the last one chosen wins, and the name to show it under
 * comes from `preload_values`, which the backend fills in for exactly this.
 */
function LinkRowValue({ filter, field, context, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  const id = Number(filter.value)
  const name = (filter.preload_values as { display_name?: unknown } | undefined)
    ?.display_name

  const held =
    Number.isSafeInteger(id) && id > 0
      ? [{ id, value: typeof name === 'string' ? name : '' }]
      : []

  return (
    <div className="flex-1">
      <LinkRowInput
        field={field}
        context={context}
        autoFocus={false}
        value={JSON.stringify(held)}
        label={t('view.filterValue', { field: field.name })}
        onValueChange={(draft) => {
          const rows = JSON.parse(draft) as { id: number }[]
          onChange(rows.length === 0 ? '' : String(rows[rows.length - 1]?.id ?? ''))
        }}
        onKeyDown={() => undefined}
      />
    </div>
  )
}

function DayOfMonthValue({ filter, field, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  const [timezone, day] = splitTimezoneAndValue(filter.value ?? '')
  const [text, type] = useDraft(day, (next) => {
    onChange(`${timezone ?? ''}${SEP}${next}`)
  })

  return (
    <Input
      type="number"
      min={1}
      max={31}
      value={text}
      aria-label={t('view.filterValue', { field: field.name })}
      onChange={(event) => {
        type(event.target.value)
      }}
      className="h-8 flex-1"
    />
  )
}

/**
 * A period, and the number or date it needs.
 *
 * Three fields in one string: `timezone?value?operator`. The timezone is kept
 * exactly as it was stored rather than recomputed, so a filter written in one
 * place goes on meaning the same thing when it is read in another.
 */
function DateValue({ filter, field, type, onChange }: FilterValueProps) {
  const { t } = useTranslation()
  const [timezone, value, operator] = splitMultiStepDateValue(filter.value ?? '')
  const refused = VIEW_FILTER_TYPES[type].refusedDateOperators ?? []
  const write = (next: { value?: string; operator?: string }): void => {
    onChange(
      `${timezone ?? ''}${SEP}${next.value ?? value}${SEP}${next.operator ?? operator}`,
    )
  }

  const [text, typeValue] = useDraft(value, (next) => {
    write({ value: next })
  })
  const takesNumber = TAKES_NUMBER.has(operator as DateOperator)

  return (
    <div className="flex flex-1 gap-1">
      <NativeSelect
        size="sm"
        className="flex-1"
        aria-label={t('view.filterPeriod', { field: field.name })}
        value={operator}
        onChange={(event) => {
          // The value is cleared with the operator: a number of weeks is not a
          // date, and carrying one into the other makes a filter that reads as
          // nonsense and matches nothing.
          write({ operator: event.target.value, value: '' })
        }}
      >
        {DATE_OPERATORS.filter((name) => !refused.includes(name)).map((name) => (
          <NativeSelectOption key={name} value={name}>
            {t(`view.datePeriod_${name}`)}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      {(takesNumber || operator === 'exact_date') && (
        <Input
          type={takesNumber ? 'number' : 'date'}
          value={text}
          aria-label={t('view.filterValue', { field: field.name })}
          onChange={(event) => {
            typeValue(event.target.value)
          }}
          className="h-8 w-32"
        />
      )}
    </div>
  )
}
